import * as React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface FieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label: string;
  hint?: string;
  error?: string;
  containerClassName?: string;
  labelTooltip?: string;
}

let fieldIdCounter = 0;
const useFieldId = (provided?: string) => {
  const generated = React.useMemo(() => `field-${++fieldIdCounter}`, []);
  return provided ?? generated;
};

const Field = React.forwardRef<HTMLInputElement, FieldProps>(
  ({ label, hint, error, id, className, containerClassName, labelTooltip, ...props }, ref) => {
    const fieldId = useFieldId(id);
    const hintId = hint ? `${fieldId}-hint` : undefined;
    const errorId = error ? `${fieldId}-error` : undefined;
    const labelNode = (
      <Label
        htmlFor={fieldId}
        className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
      >
        {label}
      </Label>
    );
    return (
      <div
        className={cn(
          "flex flex-col gap-1.5 border-b border-brand-line py-3 last:border-b-0",
          containerClassName,
        )}
      >
        {labelTooltip ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                {labelNode}
              </TooltipTrigger>
              <TooltipContent side="top">{labelTooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          labelNode
        )}
        <Input
          ref={ref}
          id={fieldId}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId ?? hintId}
          className={cn(
            "border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 text-foreground",
            className,
          )}
          {...props}
        />
        {error ? (
          <p id={errorId} className="text-xs text-destructive">
            {error}
          </p>
        ) : hint ? (
          <p id={hintId} className="text-xs text-muted-foreground">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
Field.displayName = "Field";

export { Field };